import React from 'react';
import { Reorder, useDragControls } from 'motion/react';
import { GripVertical, Trash2 } from 'lucide-react';
import { AddRowButton, IconButton, ImageField, TextInput, Toggle } from '../editor/fields';
import { GalleryItem, generateId } from './types';

// Two-column gallery grid. Each card carries an image, a label, a fallback
// icon name (used when there is no image) and a "large tile" toggle.

interface Props {
  items: GalleryItem[];
  onChange: (items: GalleryItem[]) => void;
}

export const GallerySection: React.FC<Props> = ({ items, onChange }) => {
  const update = (id: string, patch: Partial<GalleryItem>) => onChange(items.map((g) => (g.id === id ? { ...g, ...patch } : g)));
  const remove = (id: string) => onChange(items.filter((g) => g.id !== id));
  const add = () => onChange([...items, { id: generateId(), iconName: '', label: '', imageUrl: '', isLarge: false, sortOrder: items.length }]);

  return (
    <div>
      {items.length ? (
        <Reorder.Group as="div" axis="y" values={items} onReorder={onChange} className="grid gap-3 sm:grid-cols-2">
          {items.map((item) => (
            <GalleryCard key={item.id} item={item} onChange={(patch) => update(item.id, patch)} onDelete={() => remove(item.id)} />
          ))}
        </Reorder.Group>
      ) : (
        <p className="text-[12.5px] text-[color:var(--oa-muted)]">No images yet.</p>
      )}
      <div className="mt-3">
        <AddRowButton onClick={add}>Add image</AddRowButton>
      </div>
    </div>
  );
};

const GalleryCard: React.FC<{ item: GalleryItem; onChange: (patch: Partial<GalleryItem>) => void; onDelete: () => void }> = ({ item, onChange, onDelete }) => {
  const controls = useDragControls();
  return (
    <Reorder.Item as="div" value={item} dragListener={false} dragControls={controls} className="group/card rounded-xl border border-[color:var(--oa-line-2)] bg-[color:var(--oa-card)] p-3">
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          onPointerDown={(e) => controls.start(e)}
          className="grid h-6 w-6 cursor-grab touch-none place-items-center rounded text-[color:var(--oa-faint)] hover:bg-[color:var(--oa-raised-2)] hover:text-[color:var(--oa-muted)] active:cursor-grabbing"
          aria-label="Drag to reorder"
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <IconButton tone="danger" onClick={onDelete} aria-label="Remove image">
          <Trash2 className="h-3.5 w-3.5" />
        </IconButton>
      </div>
      <ImageField value={item.imageUrl || ''} onChange={(v) => onChange({ imageUrl: v })} aspect="aspect-[4/3]" placeholder="Image URL" />
      <div className="mt-2 grid grid-cols-2 gap-2">
        <TextInput value={item.label} onChange={(e) => onChange({ label: e.target.value })} placeholder="Label" />
        <TextInput value={item.iconName} onChange={(e) => onChange({ iconName: e.target.value })} placeholder="Icon, e.g. Camera" />
      </div>
      <div className="mt-2">
        <Toggle checked={item.isLarge} onChange={(v) => onChange({ isLarge: v })} label="Large tile" />
      </div>
    </Reorder.Item>
  );
};
